// Expense type configuration for record type selection
// Used by selectors, filters, and helper functions

// ============================================
// Types
// ============================================

export interface ExpenseTypeOption {
  /** Value stored in database */
  value: number;
  /** Translation key - use with t('common:expenseTypes.expense') */
  labelKey: string;
  /** Icon name for UI components */
  iconName: string;
}

/** Controls which expense types are shown */
export type ExpenseTypeMode = "all" | "core";

// ============================================
// Expense Type Options
// ============================================

// ./src/config/expenses.ts
export const EXPENSE_TYPES = {
  REFUEL: 1,
  EXPENSE: 2,
  CHECKPOINT: 3,
  TRAVEL_POINT: 4,
  REVENUE: 5,
};

export const EXPENSE_TYPE_OPTIONS: ExpenseTypeOption[] = [
  { value: EXPENSE_TYPES.EXPENSE, labelKey: "expenseTypes.expense", iconName: "Receipt" },
  { value: EXPENSE_TYPES.REFUEL, labelKey: "expenseTypes.refuel", iconName: "GasStation" },
  { value: EXPENSE_TYPES.CHECKPOINT, labelKey: "expenseTypes.checkpoint", iconName: "MapPin" },
  { value: EXPENSE_TYPES.TRAVEL_POINT, labelKey: "expenseTypes.travel", iconName: "Route" },
  { value: EXPENSE_TYPES.REVENUE, labelKey: "expenseTypes.revenue", iconName: "CashBanknote" },
];

// ============================================
// Lookup Map
// ============================================

export const EXPENSE_TYPE_MAP: Record<string, ExpenseTypeOption> =
  Object.fromEntries(EXPENSE_TYPE_OPTIONS.map((opt) => [opt.value, opt]));

// ============================================
// Mode Sets
// ============================================

/** Core types: refuel, expense, checkpoint */
const CORE_EXPENSE_TYPES: ReadonlySet<number> = new Set([
  EXPENSE_TYPES.EXPENSE,
  EXPENSE_TYPES.REFUEL,
  EXPENSE_TYPES.CHECKPOINT,
]);

// ============================================
// Helper Functions
// ============================================

/**
 * Get expense type options filtered by mode
 * @param mode - "all" returns all types, "core" returns refuel/expense/checkpoint only
 * @returns Filtered expense type options
 */
export const getExpenseTypeOptionsByMode = (
  mode: ExpenseTypeMode
): ExpenseTypeOption[] => {
  if (mode === "core") {
    return EXPENSE_TYPE_OPTIONS.filter((opt) => CORE_EXPENSE_TYPES.has(opt.value));
  }
  return EXPENSE_TYPE_OPTIONS;
};

/**
 * Get expense type option by value
 * @param value - Expense type value
 * @returns Expense type option or undefined
 */
export const getExpenseTypeOption = (
  value: number | null | undefined
): ExpenseTypeOption | undefined => {
  if (!value) return undefined;
  return EXPENSE_TYPE_MAP[value];
};

/**
 * Get icon name for an expense type
 * @param value - Expense type value
 * @returns Icon name or default
 */
export const getExpenseTypeIcon = (
  value: number | null | undefined
): string => {
  return getExpenseTypeOption(value)?.iconName ?? "Receipt";
};